import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';

import { Box, Button, TextButton } from '@zillow/constellation-10';
import { LoadingButton } from '@zg-rentals/loading-components';

import PostingActions from '../../actions/PostingActions';

import { RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import { DashboardContext } from '../../containers/DashboardPage/context';
import { default as dashboardActions } from '../../containers/DashboardPage/actions';

import { RMW_PATH } from '../../pathsManifest';

import ErrorMessagesAlert from '../../components/ErrorMessagesAlert';

import { useGetPaymentsSummary } from '../../hooks/queries/summary';

import EmptyPaymentsCard from './components/EmptyPaymentsCard';

import PaymentsMessageModule from './components/PaymentsMessageModule';
import PaymentCard from './components/PaymentCard';

import { StyledConnectedModuleCard } from './styles';

import MESSAGE_MODULE_TYPES from './MESSAGE_MODULE_TYPES';

import type { State as DashboardState } from '../../containers/DashboardPage/reducer';
import type { PaymentsSummaryResponse } from '../../hooks/queries/summary/types';

const NAVIGATION_STYLING = {
  TEXT_LINK: 'text_link',
  PRIMARY_BUTTON: 'primary_button',
  SECONDARY_BUTTON: 'secondary_button',
  TERTIARY_BUTTON: 'tertiary_button',
};

const ConnectedPayments = () => {
  const dispatch = useDispatch();
  const { dashboardState, reducerDispatch: dashboardReducerDispatch } = useContext(DashboardContext);

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const [showMessageModule, setShowMessageModule] = useState(true);

  // use isLoading condition in order apply conditional logic on the initial request
  const { data = {} as PaymentsSummaryResponse, isPending, error } = useGetPaymentsSummary(listingId);

  const { activePath: activeDashboardPath, fluidSmallLTE }: Partial<DashboardState> = dashboardState;

  const hasData = useMemo(() => {
    return data?.paymentCardList?.length > 0;
  }, [data?.paymentCardList]);

  const isThisActiveDashboardPath = useMemo(() => {
    return activeDashboardPath === RMW_PATH.DASHBOARD_PAYMENTS;
  }, [activeDashboardPath]);

  /**
   * Sets the border state for less than or equal to SM
   *
   * this component hides the border when the component is not empty
   */
  const hideBorder = useMemo(() => {
    if (fluidSmallLTE !== undefined) {
      const hideBorder = fluidSmallLTE && data?.paymentCardList?.length > 0;

      return hideBorder;
    }
  }, [fluidSmallLTE, data?.paymentCardList?.length]);

  useEffect(() => {
    if (isThisActiveDashboardPath === false) {
      // do not set dashboard state fluid value if the active path is not leases module
      return;
    }

    dashboardActions.updateAnyProperty({
      reducerDispatch: dashboardReducerDispatch,
      data: {
        fluidSmallLTE: hasData,
      },
    });
  }, [activeDashboardPath, isPending, error]);

  if (error?.error?.data?.partialMessages) {
    return (
      <StyledConnectedModuleCard title="Payments">
        <ErrorMessagesAlert appearance="warning" errorMessages={error.error.data.partialMessages} />
      </StyledConnectedModuleCard>
    );
  }

  const closeMessageModule = (actionId: string) => {
    dispatch(PostingActions.property.markAcknowledgedLandlordMessage(actionId))
      // @ts-expect-error
      .then(() => {
        setShowMessageModule(false);
      })
      .catch(() => {
        setShowMessageModule(false);
      });
  };

  const renderTopNavigation = (navigation: PaymentsSummaryResponse['navigation']) => {
    if (navigation && navigation.topNavigation) {
      const { text, url, styling } = navigation.topNavigation;

      const fontTypeMediaQuery = 'bodySmallHeading';

      if (styling === NAVIGATION_STYLING.PRIMARY_BUTTON) {
        return (
          <Button asChild size="sm" tone="brand" emphasis="filled">
            <a href={url}>{text}</a>
          </Button>
        );
      }

      if (styling === NAVIGATION_STYLING.SECONDARY_BUTTON) {
        return (
          <Button asChild size="sm" tone="brand" emphasis="outlined">
            <a href={url}>{text}</a>
          </Button>
        );
      }

      if (styling === NAVIGATION_STYLING.TERTIARY_BUTTON) {
        return (
          <Button asChild size="sm" tone="neutral" emphasis="outlined">
            <a href={url}>{text}</a>
          </Button>
        );
      }

      return (
        <TextButton asChild>
          <a href={url}>{text}</a>
        </TextButton>
      );
    }

    if (isPending) {
      return (
        <LoadingButton isLoading={isPending} loadingWidth="20vw">
          &nbsp;
        </LoadingButton>
      );
    }

    return null;
  };

  const renderBottomNavigation = (navigation: PaymentsSummaryResponse['navigation']) => {
    if (navigation && navigation.bottomNavigationList && navigation.bottomNavigationList.length) {
      return (
        <Box
          data-testid="renderBottomNavigation"
          css={{
            display: 'flex',
            justifyContent: 'left',
            alignItems: 'center',
            flexDirection: { base: 'column-reverse', md: 'row-reverse' },
          }}
        >
          {navigation.bottomNavigationList
            .map(({ text, url, styling }: { text: string; url: string; styling: string }) => {
              if (styling === NAVIGATION_STYLING.PRIMARY_BUTTON) {
                return (
                  <Box
                    css={{
                      marginRight: { base: '0', md: '400' },
                      marginBottom: { base: '400', md: '0' },
                    }}
                    key={`ConnectedPayments-BottomNavigation-${text}`}
                  >
                    <Button asChild tone="brand" emphasis="filled" size="md" fluid={true}>
                      <a href={url}>{text}</a>
                    </Button>
                  </Box>
                );
              }

              if (styling === NAVIGATION_STYLING.SECONDARY_BUTTON) {
                return (
                  <Box
                    css={{ marginRight: { base: '0', md: '400' } }}
                    key={`ConnectedPayments-BottomNavigation-${text}`}
                  >
                    <Button asChild tone="brand" emphasis="outlined" size="md" fluid={true}>
                      <a href={url}>{text}</a>
                    </Button>
                  </Box>
                );
              }

              return (
                <Box css={{ marginRight: { base: '0', md: '400' } }} key={`ConnectedPayments-BottomNavigation-${text}`}>
                  <TextButton asChild>
                    <a href={url}>{text}</a>
                  </TextButton>
                </Box>
              );
            })
            .map((navComponent) => (
              <Box
                css={{
                  width: { base: '100%', md: 'initial' },
                }}
                key={`ConnectedPayments-BottomNavigation-${navComponent.key}`}
              >
                {navComponent}
              </Box>
            ))}
        </Box>
      );
    }

    if (isPending) {
      return (
        <LoadingButton isLoading={isPending} loadingWidth="40%">
          &nbsp;
        </LoadingButton>
      );
    }

    return null;
  };

  // @ts-expect-error
  const renderMessageModule = (messageModule) => {
    if (!messageModule || !showMessageModule) {
      return null;
    }

    return <PaymentsMessageModule {...messageModule} closeMessageModule={closeMessageModule} />;
  };

  const renderPaymentCards = (paymentCardList: []) => {
    const paymentCardsToRender = isPending ? Array(4).fill({}) : paymentCardList;

    if (!paymentCardsToRender) {
      return [];
    }

    // TODO: update to use a Card type
    return paymentCardsToRender.map((card: PaymentsSummaryResponse['paymentCardList'][number]) => (
      <PaymentCard {...card} isLoading={isPending} key={`PaymentCard-${card?.title}`} />
    ));
  };

  const createPaymentCardList = (cards: []) =>
    cards
      .filter((card) => Boolean(card))
      .map((card, i) => (
        <Box
          className={cx('PaymentCard', {
            'PaymentCard-Even': i % 2 === 0,
          })}
          css={{
            minWidth: 0,
            flex: '0 50%',
            '&.PaymentCard-Even': {
              paddingRight: { md: '400' },
            },
          }}
          key={`FlexPaymentCard-${i}`}
        >
          {card}
        </Box>
      ));

  const { messageModule, navigation, paymentCardList, title } = data;

  return (
    <>
      {messageModule && messageModule.type === MESSAGE_MODULE_TYPES.ONBOARDING ? (
        <Box className="PaymentCard" css={{ flex: '1 50%' }}>
          <EmptyPaymentsCard {...messageModule} />
        </Box>
      ) : (
        <StyledConnectedModuleCard
          title={title || 'Payments'}
          ctaComponent={renderTopNavigation(navigation)}
          mediaQuery={{
            xs: { hideBorder },
            sm: { hideBorder },
            md: { hideBorder: false },
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flex: '1 0' }}>
            <Box
              css={{
                display: 'flex',
                flexDirection: { base: 'column', md: 'row' },
                flexWrap: { md: 'wrap' },
              }}
            >
              {/* @ts-expect-error */}
              {createPaymentCardList([renderMessageModule(messageModule), ...renderPaymentCards(paymentCardList)])}
            </Box>
            {Boolean(navigation || isPending) && (
              <Box css={{ paddingTop: { base: '200', lg: '400' } }}>{renderBottomNavigation(navigation)}</Box>
            )}
          </Box>
        </StyledConnectedModuleCard>
      )}
    </>
  );
};

export default ConnectedPayments;
